import { SelectionEventType, TranscriptSelectionManager, ElementSelectionData } from './TranscriptSelectionManager';
import { SelectionService } from '../services/SelectionService';
import { TextElement, PunctElement } from '../models/TranscriptElement';
import { TranscriptContainer } from '../models/TranscriptContainer';

/**
 * Element lookup entry for the ID map
 */
interface ElementLookup {
  segmentId: string;
  paragraphId: string;
  element: TextElement | PunctElement;
}

/**
 * Adapter that connects TranscriptSelectionManager (DOM-based) with 
 * SelectionService (model-based)
 * 
 * This adapter provides bidirectional synchronization between DOM selections
 * and the app selection state. It ensures changes in either system are
 * reflected in the other, creating a unified selection experience.
 */
export class SelectionServiceAdapter {
  /** DOM-based selection manager */
  private selectionManager: TranscriptSelectionManager;
  
  /** Model-based selection service */
  private selectionService: SelectionService;
  
  /** Transcript container with all elements */
  private container: TranscriptContainer;
  
  /** Flag to prevent infinite loops during bidirectional updates */
  private isUpdatingFromDom = false;
  private isUpdatingFromService = false;
  
  /** ID lookup map for faster element access */
  private idMap: Map<string, ElementLookup> = new Map();

  /**
   * Create a new adapter between selection manager and service
   * 
   * @param selectionManager The DOM-based selection manager
   * @param selectionService The model-based selection service
   * @param container The transcript container with all segments and elements
   */
  constructor(
    selectionManager: TranscriptSelectionManager,
    selectionService: SelectionService,
    container: TranscriptContainer
  ) {
    this.selectionManager = selectionManager;
    this.selectionService = selectionService;
    this.container = container;

    // Register for selection manager events
    this.selectionManager.addEventListener('selectionComplete', this.handleSelectionComplete);
    this.selectionManager.addEventListener('selectionClear', this.handleSelectionClear);
    this.selectionManager.addEventListener('rightClick', this.handleSelectionComplete);

    // Subscribe to selection service changes
    this.selectionService.registerForSelectionUpdates(this.handleServiceSelectionUpdate);
    
    // Build the initial ID map
    this.updateContainer(container);
  }
  
  /**
   * Updates the container reference and rebuilds the ID map
   * Call this whenever the container structure changes
   * 
   * @param container The new transcript container
   */
  updateContainer(container: TranscriptContainer): void {
    this.container = container;
    this.rebuildIdMap();
  }
  
  /**
   * Rebuilds the ID lookup map for fast element access
   */
  private rebuildIdMap(): void {
    // Clear existing map
    this.idMap.clear();
    
    // Build lookup map from container structure
    if (this.container) {
      this.container.segments.forEach(segment => {
        segment.paragraphs.forEach(paragraph => {
          paragraph.elements.forEach(element => {
            if (element.id) {
              this.idMap.set(element.id, {
                segmentId: segment.id,
                paragraphId: paragraph.id,
                element: element as TextElement | PunctElement
              });
            }
          });
        });
      });
    }
  }

  /**
   * Clean up all listeners when component unmounts
   */
  cleanup() {
    // Remove selection manager listeners
    this.selectionManager.removeEventListener('selectionComplete', this.handleSelectionComplete);
    this.selectionManager.removeEventListener('selectionClear', this.handleSelectionClear);
    this.selectionManager.removeEventListener('rightClick', this.handleSelectionComplete);
    
    // Note: We can't easily remove the selection service listener
    // The API doesn't support unregistering a specific listener,
    // but this is a minor memory leak since components are typically unmounted together
  }

  /**
   * Handle selection completion from the DOM-based selection manager
   * Syncs from DOM to model
   */
  private handleSelectionComplete = (eventType: SelectionEventType, data: any) => {
    // Prevent recursive updates when selection is coming from service
    if (this.isUpdatingFromService) return;
    
    this.isUpdatingFromDom = true;
    
    try {
      // Get selection data from the event
      const selectionData: ElementSelectionData[] | null = 
        data?.selectedElements || this.selectionManager.getSelectionData();
      
      if (!selectionData || selectionData.length === 0) {
        this.selectionService.clearAllSelections();
        return;
      }

      // Convert DOM selection to model references
      const modelElements = this.getModelElementsFromSelectionData(selectionData);
      
      // Update selection service if we found valid elements
      if (modelElements && modelElements.length > 0) {
        this.selectionService.clearAllSelections();
        this.selectionService.selectElements(modelElements as any, true);
      }
    } finally {
      this.isUpdatingFromDom = false;
    }
  };

  /**
   * Handle selection clearing from the DOM-based selection manager
   */
  private handleSelectionClear = () => {
    // Prevent recursive updates when selection is coming from service
    if (this.isUpdatingFromService) return;
    
    this.isUpdatingFromDom = true;
    
    try {
      this.selectionService.clearAllSelections();
    } finally {
      this.isUpdatingFromDom = false;
    }
  };

  /**
   * Handle selection updates from the selection service (model-based)
   * Syncs from model to DOM
   */
  private handleServiceSelectionUpdate = (selectedElements: any[]) => {
    // Prevent recursive updates when selection is coming from DOM
    if (this.isUpdatingFromDom) return;
    
    this.isUpdatingFromService = true;
    
    try {
      // Simply update the DOM selection directly without comparison
      // This is more efficient and avoids unnecessary re-renders
      
      // Convert selection service elements to IDs
      const serviceIds = selectedElements.map(e => e.element?.id).filter(id => id);
      
      // Update the DOM selection directly
      // The DOM manager itself will handle not updating if nothing changed
      if (serviceIds.length > 0) {
        this.selectionManager.selectElementsByIds(serviceIds);
      } else {
        this.selectionManager.clearSelection();
      }
    } finally {
      this.isUpdatingFromService = false;
    }
  };

  /**
   * Convert DOM-based selection data to model elements
   * 
   * @param selectionData The DOM selection data to convert
   * @returns Array of model elements with their parent references
   */
  private getModelElementsFromSelectionData(selectionData: ElementSelectionData[]) {
    return selectionData
      .map(data => {
        try {
          // Find segment
          const segment = this.container.getSegment(data.segmentId);
          if (!segment) return null;
          
          // Find paragraph
          const paragraph = segment.getParagraph(data.paragraphId);
          if (!paragraph) return null;
          
          // Find element
          const element = paragraph.getElement(data.id) as TextElement | PunctElement;
          if (!element) return null;
          
          return {
            element,
            segmentId: data.segmentId,
            paragraphId: data.paragraphId
          };
        } catch (e) {
          console.error('Error finding model element:', e);
          return null;
        }
      })
      .filter(el => el !== null);
  }

  /**
   * Get element IDs from model-based selection
   * 
   * @param elements The model elements
   * @returns Array of element IDs
   */
  private getElementIdsFromModelElements(elements: any[]) {
    return elements
      .map(el => el.element?.id)
      .filter(id => id);
  }
}